import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createClubPalmares,
  createClubTrophy,
  deleteClubPalmares,
  deleteClubTrophy,
  fetchClubPalmares,
  fetchClubTrophies,
  updateClubPalmares,
  updateClubTrophy,
  uploadAdminMedia
} from "@/features/admin/api";
import type { AdminClubPalmares, AdminClubTrophy } from "@/features/admin/types";
import { useAdminSession } from "@/features/admin/useAdminSession";

type ClubView = "trophees" | "palmares";

const EMPTY_TROPHY_FORM = {
  titre: "",
  sport: "",
  annee: "",
  rang: "1er",
  ordre: 0
};

const EMPTY_PALMARES_FORM = {
  annee: "",
  titre: "",
  sport: "",
  rang: "Champion",
  ordre: 0
};

export default function ClubTab() {
  const { request } = useAdminSession();
  const [view, setView] = useState<ClubView>("trophees");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [trophies, setTrophies] = useState<AdminClubTrophy[]>([]);
  const [palmares, setPalmares] = useState<AdminClubPalmares[]>([]);
  const [savingTrophy, setSavingTrophy] = useState(false);
  const [savingPalmares, setSavingPalmares] = useState(false);
  const [editingTrophyId, setEditingTrophyId] = useState<string | null>(null);
  const [editingPalmaresId, setEditingPalmaresId] = useState<string | null>(null);
  const [selectedTrophy, setSelectedTrophy] = useState<AdminClubTrophy | null>(null);
  const [trophyForm, setTrophyForm] = useState(EMPTY_TROPHY_FORM);
  const [palmaresForm, setPalmaresForm] = useState(EMPTY_PALMARES_FORM);
  const [trophyFile, setTrophyFile] = useState<File | null>(null);
  const [trophyPreview, setTrophyPreview] = useState<string>("");

  const showToast = useCallback((msg: string, type: "success" | "error") => {
    setToast({ msg, type });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const loadClubData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [nextTrophies, nextPalmares] = await Promise.all([
        fetchClubTrophies(request),
        fetchClubPalmares(request)
      ]);
      setTrophies(nextTrophies);
      setPalmares(nextPalmares);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible de charger les trophées et le palmarès.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadClubData();
  }, [loadClubData]);

  useEffect(() => {
    if (!trophyFile) {
      return;
    }

    const previewUrl = URL.createObjectURL(trophyFile);
    setTrophyPreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [trophyFile]);

  const sortedTrophies = useMemo(
    () => [...trophies].sort((first, second) => first.ordre - second.ordre || Number(second.annee) - Number(first.annee)),
    [trophies]
  );

  const sortedPalmares = useMemo(
    () => [...palmares].sort((first, second) => Number(second.annee) - Number(first.annee) || first.ordre - second.ordre),
    [palmares]
  );

  const resetTrophyForm = () => {
    setEditingTrophyId(null);
    setTrophyForm(EMPTY_TROPHY_FORM);
    setTrophyFile(null);
    setTrophyPreview("");
  };

  const resetPalmaresForm = () => {
    setEditingPalmaresId(null);
    setPalmaresForm(EMPTY_PALMARES_FORM);
  };

  const handleEditTrophy = (trophy: AdminClubTrophy) => {
    setEditingTrophyId(trophy.id);
    setTrophyForm({
      titre: trophy.titre,
      sport: trophy.sport,
      annee: trophy.annee,
      rang: trophy.rang,
      ordre: trophy.ordre
    });
    setTrophyPreview(trophy.image);
    setTrophyFile(null);
    setView("trophees");
  };

  const handleEditPalmares = (item: AdminClubPalmares) => {
    setEditingPalmaresId(item.id);
    setPalmaresForm({
      annee: item.annee,
      titre: item.titre,
      sport: item.sport,
      rang: item.rang,
      ordre: item.ordre
    });
    setView("palmares");
  };

  const handleSaveTrophy = async () => {
    if (!trophyForm.titre.trim() || !trophyForm.sport.trim() || !trophyForm.annee.trim()) {
      setError("Complète la description, la discipline et l'année du trophée.");
      return;
    }

    setSavingTrophy(true);
    setError(null);

    try {
      let nextImage = trophyPreview;
      let nextImagePublicId: string | null | undefined = undefined;
      let nextImageResourceType: string | null | undefined = undefined;

      if (trophyFile) {
        const [asset] = await uploadAdminMedia(request, [trophyFile], "asc-sonatel/club/trophies");
        if (!asset) {
          throw new Error("L'image du trophée n'a pas pu être envoyée.");
        }
        nextImage = asset.url;
        nextImagePublicId = asset.publicId;
        nextImageResourceType = asset.resourceType;
      }

      if (!nextImage) {
        setError("Ajoute l'image du trophée avant d'enregistrer.");
        setSavingTrophy(false);
        return;
      }

      if (editingTrophyId) {
        await updateClubTrophy(request, editingTrophyId, {
          ...trophyForm,
          image: nextImage,
          ...(nextImagePublicId !== undefined ? { imagePublicId: nextImagePublicId } : {}),
          ...(nextImageResourceType !== undefined ? { imageResourceType: nextImageResourceType } : {})
        });
        showToast("Trophée mis à jour.", "success");
      } else {
        await createClubTrophy(request, {
          ...trophyForm,
          image: nextImage,
          imagePublicId: nextImagePublicId ?? null,
          imageResourceType: nextImageResourceType ?? null
        });
        showToast("Trophée ajouté.", "success");
      }

      resetTrophyForm();
      await loadClubData();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement du trophée impossible.";
      setError(message);
      showToast(message, "error");
    } finally {
      setSavingTrophy(false);
    }
  };

  const handleSavePalmares = async () => {
    if (!palmaresForm.annee.trim() || !palmaresForm.titre.trim() || !palmaresForm.sport.trim() || !palmaresForm.rang.trim()) {
      setError("Complète l'année, le titre, le sport et le résultat du palmarès.");
      return;
    }

    setSavingPalmares(true);
    setError(null);

    try {
      if (editingPalmaresId) {
        await updateClubPalmares(request, editingPalmaresId, palmaresForm);
        showToast("Palmarès mis à jour.", "success");
      } else {
        await createClubPalmares(request, palmaresForm);
        showToast("Palmarès ajouté.", "success");
      }

      resetPalmaresForm();
      await loadClubData();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement du palmarès impossible.";
      setError(message);
      showToast(message, "error");
    } finally {
      setSavingPalmares(false);
    }
  };

  const handleDeleteTrophy = async (trophyId: string) => {
    try {
      await deleteClubTrophy(request, trophyId);
      showToast("Trophée supprimé.", "success");
      if (editingTrophyId === trophyId) {
        resetTrophyForm();
      }
      await loadClubData();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression du trophée impossible.";
      setError(message);
      showToast(message, "error");
    }
  };

  const handleDeletePalmares = async (palmaresId: string) => {
    try {
      await deleteClubPalmares(request, palmaresId);
      showToast("Palmarès supprimé.", "success");
      if (editingPalmaresId === palmaresId) {
        resetPalmaresForm();
      }
      await loadClubData();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression du palmarès impossible.";
      setError(message);
      showToast(message, "error");
    }
  };

  return (
    <div className="relative space-y-6">
      {toast ? (
        <div className={`fixed right-6 top-6 z-50 rounded-xl px-5 py-3 text-sm font-medium text-white shadow-lg ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}>
          {toast.msg}
        </div>
      ) : null}

      {selectedTrophy ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedTrophy(null)}
        >
          <div
            className="relative w-full max-w-3xl rounded-[32px] bg-white p-6 shadow-[0_28px_80px_rgba(0,0,0,0.25)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedTrophy(null)}
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition hover:bg-gray-200 hover:text-gray-700"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            <div className="grid gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] md:items-center">
              <div className="flex min-h-[320px] items-center justify-center rounded-[28px] bg-[#FAF9F7] p-6">
                <img src={selectedTrophy.image} alt={selectedTrophy.titre} className="max-h-[420px] w-auto max-w-full object-contain" />
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E8521A]">Trophée sélectionné</p>
                <h2 className="text-2xl font-bold text-[#1A1A1A]">{selectedTrophy.titre}</h2>
                <p className="text-base text-gray-500">{selectedTrophy.sport}</p>
                <p className="text-sm font-medium text-gray-400">Année {selectedTrophy.annee}</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Trophées & Palmarès</h1>
        <p className="mt-1 text-sm text-gray-500">Publie les trophées gagnés et le palmarès officiel visibles sur la page Club.</p>
        {error ? <p className="mt-2 text-sm text-red-500">{error}</p> : null}
      </div>

      <div className="flex gap-2 rounded-full bg-[#FAF9F7] p-1 w-fit">
        {([
          { id: "trophees", label: "Trophées" },
          { id: "palmares", label: "Palmarès" }
        ] as const).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setView(item.id)}
            className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${view === item.id ? "bg-black text-white" : "text-gray-600 hover:text-black"}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {view === "trophees" ? (
        <>
          <div className="rounded-3xl border border-gray-100 bg-white p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-[#1A1A1A]">{editingTrophyId ? "Modifier le trophée" : "Ajouter un trophée"}</h2>
                <p className="text-sm text-gray-500">L'image et la disposition publique imitent la page Club.</p>
              </div>
              {editingTrophyId ? (
                <button type="button" onClick={resetTrophyForm} className="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
                  Annuler l'édition
                </button>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <input value={trophyForm.titre} onChange={(event) => setTrophyForm((current) => ({ ...current, titre: event.target.value }))} placeholder="Description du trophée" className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none" />
              <input value={trophyForm.sport} onChange={(event) => setTrophyForm((current) => ({ ...current, sport: event.target.value }))} placeholder="Discipline" className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none" />
              <input value={trophyForm.annee} onChange={(event) => setTrophyForm((current) => ({ ...current, annee: event.target.value }))} placeholder="Année" className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none" />
              <label className="flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-orange-200 bg-orange-50/50 px-4 py-3 text-sm font-medium text-[#E8521A] hover:bg-orange-50">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => setTrophyFile(event.target.files?.[0] ?? null)}
                />
                {trophyFile ? "Changer l'image du trophée" : editingTrophyId ? "Remplacer l'image du trophée" : "Choisir l'image du trophée"}
              </label>
            </div>

            {trophyPreview ? (
              <div className="mt-5 flex items-center gap-4 rounded-2xl bg-[#FAF9F7] p-4">
                <div className="flex h-28 w-28 items-center justify-center rounded-2xl bg-white p-3 shadow-sm">
                  <img src={trophyPreview} alt="Aperçu trophée" className="max-h-full w-auto max-w-full object-contain" />
                </div>
                <p className="text-sm text-gray-600">Aperçu de l'image qui sera utilisée sur la page Club.</p>
              </div>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => void handleSaveTrophy()}
                disabled={savingTrophy}
                className="rounded-xl bg-[#E8521A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingTrophy ? "Enregistrement..." : editingTrophyId ? "Mettre à jour le trophée" : "Publier le trophée"}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-72 animate-pulse rounded-3xl border border-gray-100 bg-white"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {sortedTrophies.map((trophy) => (
                <div key={trophy.id} className="rounded-3xl border border-gray-100 bg-white p-6 text-center">
                  <button
                    type="button"
                    onClick={() => setSelectedTrophy(trophy)}
                    className="mx-auto mb-5 flex h-44 w-full items-center justify-center overflow-hidden rounded-[28px] bg-[#FAF9F7] p-4 transition hover:bg-orange-50"
                  >
                    <img src={trophy.image} alt={trophy.titre} className="max-h-full w-auto max-w-full object-contain" />
                  </button>
                  <h3 className="text-base font-bold text-[#1A1A1A]">{trophy.titre}</h3>
                  <p className="mt-1 text-sm text-gray-500">{trophy.sport}</p>
                  <p className="mt-1 text-sm text-gray-400">{trophy.annee}</p>
                  <button
                    type="button"
                    onClick={() => setSelectedTrophy(trophy)}
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#E8521A] transition hover:text-[#c94816]"
                  >
                    Voir le trophée
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <div className="mt-5 flex justify-center gap-2">
                    <button type="button" onClick={() => handleEditTrophy(trophy)} className="rounded-xl border border-orange-200 px-4 py-2 text-sm text-[#E8521A] hover:bg-orange-50">
                      Modifier
                    </button>
                    <button type="button" onClick={() => void handleDeleteTrophy(trophy.id)} className="rounded-xl border border-red-200 px-4 py-2 text-sm text-red-500 hover:bg-red-50">
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
              {!sortedTrophies.length ? (
                <div className="col-span-full rounded-3xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
                  Aucun trophée publié pour le moment.
                </div>
              ) : null}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="rounded-3xl border border-gray-100 bg-white p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-[#1A1A1A]">{editingPalmaresId ? "Modifier une ligne du palmarès" : "Ajouter au palmarès"}</h2>
                <p className="text-sm text-gray-500">Le tableau public sera alimenté automatiquement avec ces lignes.</p>
              </div>
              {editingPalmaresId ? (
                <button type="button" onClick={resetPalmaresForm} className="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
                  Annuler l'édition
                </button>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <input value={palmaresForm.annee} onChange={(event) => setPalmaresForm((current) => ({ ...current, annee: event.target.value }))} placeholder="Année" className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none" />
              <input value={palmaresForm.titre} onChange={(event) => setPalmaresForm((current) => ({ ...current, titre: event.target.value }))} placeholder="Compétition" className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none" />
              <input value={palmaresForm.sport} onChange={(event) => setPalmaresForm((current) => ({ ...current, sport: event.target.value }))} placeholder="Sport" className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none" />
              <input value={palmaresForm.rang} onChange={(event) => setPalmaresForm((current) => ({ ...current, rang: event.target.value }))} placeholder="Résultat : Champion, Vice-Champion..." className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none" />
              <input type="number" min={0} value={palmaresForm.ordre} onChange={(event) => setPalmaresForm((current) => ({ ...current, ordre: Number(event.target.value) || 0 }))} placeholder="Ordre" className="rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none" />
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => void handleSavePalmares()}
                disabled={savingPalmares}
                className="rounded-xl bg-[#E8521A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingPalmares ? "Enregistrement..." : editingPalmaresId ? "Mettre à jour le palmarès" : "Ajouter au palmarès"}
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAF9F7]">
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider text-gray-500">Année</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider text-gray-500">Compétition</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider text-gray-500">Sport</th>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider text-gray-500">Résultat</th>
                  <th className="px-6 py-4 text-right text-sm font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedPalmares.map((item, index) => (
                  <tr key={item.id} className={`border-t border-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}`}>
                    <td className="px-6 py-4 text-sm font-bold text-[#E8521A]">{item.annee}</td>
                    <td className="px-6 py-4 text-sm font-medium text-[#1A1A1A]">{item.titre}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{item.sport}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.rang}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => handleEditPalmares(item)} className="rounded-xl border border-orange-200 px-4 py-2 text-sm text-[#E8521A] hover:bg-orange-50">
                          Modifier
                        </button>
                        <button type="button" onClick={() => void handleDeletePalmares(item.id)} className="rounded-xl border border-red-200 px-4 py-2 text-sm text-red-500 hover:bg-red-50">
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!sortedPalmares.length ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                      Aucun palmarès publié pour le moment.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
